import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Plus, X } from "lucide-react";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  parseISO,
} from "date-fns";
import { it as itLocale } from "date-fns/locale";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLOR = {
  confirmed: "bg-green-500",
  pending_payment: "bg-amber-500",
  cancelled: "bg-red-400",
};

export default function CalendarView({ bookings, services, authHeaders, onChange }) {
  const [cursor, setCursor] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
    const arr = [];
    let d = start;
    while (d <= end) {
      arr.push(d);
      d = addDays(d, 1);
    }
    return arr;
  }, [cursor]);

  const byDate = useMemo(() => {
    const m = new Map();
    bookings.forEach((b) => {
      if (!m.has(b.booking_date)) m.set(b.booking_date, []);
      m.get(b.booking_date).push(b);
    });
    // Sort by time inside each day
    m.forEach((arr) => arr.sort((a, b) => a.pickup_time.localeCompare(b.pickup_time)));
    return m;
  }, [bookings]);

  const dayBookings = selectedDay
    ? byDate.get(format(selectedDay, "yyyy-MM-dd")) || []
    : [];

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-6">
      <div className="bg-white rounded-2xl p-5 border border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCursor(subMonths(cursor, 1))}
            data-testid="calendar-prev-btn"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="font-display text-xl brand-text capitalize">
            {format(cursor, "MMMM yyyy", { locale: itLocale })}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCursor(addMonths(cursor, 1))}
            data-testid="calendar-next-btn"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-xs text-slate-500 mb-2">
          {["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map((d) => (
            <div key={d} className="text-center py-1">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days.map((d) => {
            const key = format(d, "yyyy-MM-dd");
            const list = byDate.get(key) || [];
            const inMonth = isSameMonth(d, cursor);
            const isSel = selectedDay && isSameDay(d, selectedDay);
            return (
              <button
                key={key}
                data-testid={`calendar-day-${key}`}
                onClick={() => {
                  setSelectedDay(d);
                  setShowForm(false);
                }}
                className={`relative aspect-square rounded-lg p-1.5 text-left transition-colors border ${
                  isSel
                    ? "border-[#0A2463] bg-[#0A2463]/5"
                    : "border-transparent hover:border-slate-200"
                } ${inMonth ? "" : "opacity-40"}`}
              >
                <div className="text-xs font-medium brand-text">{format(d, "d")}</div>
                {list.length > 0 && (
                  <div className="absolute bottom-1 left-1 right-1 flex flex-wrap gap-0.5">
                    {list.slice(0, 4).map((b) => (
                      <span
                        key={b.id}
                        className={`w-1.5 h-1.5 rounded-full ${
                          STATUS_COLOR[b.status] || "bg-slate-400"
                        }`}
                      />
                    ))}
                    {list.length > 4 && (
                      <span className="text-[9px] text-slate-500">+{list.length - 4}</span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <div className="mt-4 flex gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-green-500" /> Confermata
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-amber-500" /> Nuova
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-red-400" /> Annullata
          </span>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-5 border border-slate-200 h-fit">
        {!selectedDay && (
          <div className="text-slate-500 text-sm py-10 text-center">
            Seleziona un giorno per vedere o aggiungere prenotazioni
          </div>
        )}
        {selectedDay && !showForm && (
          <>
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-lg brand-text capitalize">
                {format(selectedDay, "EEEE d MMMM", { locale: itLocale })}
              </div>
              <Button
                size="sm"
                onClick={() => setShowForm(true)}
                data-testid="calendar-add-manual-btn"
                className="brand-bg text-white rounded-full"
              >
                <Plus className="w-4 h-4 mr-1" /> Manuale
              </Button>
            </div>
            {dayBookings.length === 0 && (
              <div className="text-slate-400 text-sm py-6 text-center">Nessuna prenotazione</div>
            )}
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {dayBookings.map((b) => (
                <div
                  key={b.id}
                  data-testid={`calendar-booking-${b.id}`}
                  className="p-3 rounded-lg border border-slate-200 text-sm"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono brand-text">{b.pickup_time}</span>
                    <span
                      className={`w-2 h-2 rounded-full ${STATUS_COLOR[b.status] || "bg-slate-400"}`}
                    />
                  </div>
                  <div className="font-medium mt-1">{b.customer_name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{b.service_name}</div>
                  <div className="text-xs text-slate-500 mt-1">
                    📍 {b.pickup_location} → {b.dropoff_location}
                  </div>
                  {b.customer_phone && (
                    <a
                      href={`tel:${b.customer_phone}`}
                      className="text-xs brand-text underline mt-1 inline-block"
                    >
                      📞 {b.customer_phone}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
        {selectedDay && showForm && (
          <ManualForm
            day={selectedDay}
            services={services}
            authHeaders={authHeaders}
            onCancel={() => setShowForm(false)}
            onDone={() => {
              setShowForm(false);
              onChange();
            }}
          />
        )}
      </div>
    </div>
  );
}

function ManualForm({ day, services, authHeaders, onCancel, onDone }) {
  const [form, setForm] = useState({
    service_id: "",
    pickup_time: "10:00",
    customer_name: "",
    customer_phone: "",
    pickup_location: "",
    dropoff_location: "",
    passengers: 1,
    luggage: 0,
    price_eur: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name) {
      toast.error("Nome richiesto");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/bookings/manual`,
        {
          ...form,
          booking_date: format(day, "yyyy-MM-dd"),
          passengers: Number(form.passengers),
          luggage: Number(form.luggage),
          price_eur: form.price_eur ? Number(form.price_eur) : null,
          service_id: form.service_id || null,
        },
        { headers: authHeaders() },
      );
      toast.success("Aggiunta");
      onDone();
    } catch (err) {
      toast.error("Errore");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="manual-booking-form" className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="font-display text-lg brand-text">
          Nuova · {format(day, "d MMM", { locale: itLocale })}
        </div>
        <button type="button" onClick={onCancel} className="text-slate-400">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div>
        <Label>Servizio</Label>
        <Select
          value={form.service_id}
          onValueChange={(v) => setForm({ ...form, service_id: v })}
        >
          <SelectTrigger data-testid="manual-service-select">
            <SelectValue placeholder="Manuale / libero" />
          </SelectTrigger>
          <SelectContent>
            {services.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.name_it} — €{s.price_eur.toFixed(0)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label>Ora</Label>
          <Input
            type="time"
            data-testid="manual-time-input"
            value={form.pickup_time}
            onChange={(e) => setForm({ ...form, pickup_time: e.target.value })}
          />
        </div>
        <div>
          <Label>Prezzo €</Label>
          <Input
            type="number"
            data-testid="manual-price-input"
            value={form.price_eur}
            onChange={(e) => setForm({ ...form, price_eur: e.target.value })}
            placeholder="auto"
          />
        </div>
      </div>
      <div>
        <Label>Cliente</Label>
        <Input
          data-testid="manual-name-input"
          value={form.customer_name}
          onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
          required
        />
      </div>
      <div>
        <Label>Telefono</Label>
        <Input
          data-testid="manual-phone-input"
          value={form.customer_phone}
          onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
        />
      </div>
      <div>
        <Label>Pickup</Label>
        <Input
          data-testid="manual-pickup-input"
          value={form.pickup_location}
          onChange={(e) => setForm({ ...form, pickup_location: e.target.value })}
        />
      </div>
      <div>
        <Label>Dropoff</Label>
        <Input
          data-testid="manual-dropoff-input"
          value={form.dropoff_location}
          onChange={(e) => setForm({ ...form, dropoff_location: e.target.value })}
        />
      </div>
      <div>
        <Label>Note</Label>
        <Textarea
          rows={2}
          data-testid="manual-notes-input"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
      </div>
      <Button
        type="submit"
        disabled={saving}
        data-testid="manual-save-btn"
        className="brand-bg text-white rounded-full w-full"
      >
        Salva
      </Button>
    </form>
  );
}
